if (typeof ProductVariants !== 'function') {
  class ProductVariants extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.id;
      this.hasDynamicLabels = this.dataset.dynamicLabels === 'true';
      this.variantRequired = this.dataset.variantRequired === 'true';
      this.noHistory =
        this.hasAttribute('data-no-history') || !!this.closest('product-page')?.hasAttribute('data-no-history');
      this.addEventListener('change', (e) => this.onVariantChange(e));
      this.template = this.dataset.template;

      this._variantChange = new Event('VARIANT_CHANGE');
      this._variantWorking = new Event('variant:working');
      this._variantChanged = new Event('variant:changed');
      this._combinedListingWorking = new Event('combined-listing:working');
      this._combinedListingChanged = new Event('combined-listing:changed');
      this._fetchController = null;

      this.updateOptionsAvailability();
    }

    async onVariantChange(e) {
      this.element = e.target;
      this.dispatchEvent(this._variantWorking);
      const sectionId = this.dataset.scriptsSection || 'product-scripts';
      const combinedListing = (e.target.dataset.productUrl || '').length > 0;
      const inlineCombinedListing = this.dataset.inlineCombinedListing === 'true' && combinedListing;

      this.optimisticUpdateLabels(e);

      if (inlineCombinedListing) return this.onInlineCombinedListingChange(e);
      // Exit if combined listing
      if (combinedListing) return this.onCombinedListingChange(e);

      // Updates URL With Option Values
      const optionValues = this.selectedOptionValues;
      let url = new URL(`${window.location.origin}${this.dataset.url}`);
      url.searchParams.set('option_values', optionValues);

      // Update browser URL unless inside a modal
      if (!this.noHistory) {
        window.history.replaceState({}, '', url.toString());
      }

      // update URL with Section ID
      url.searchParams.set('section_id', sectionId);

      let sectionContent;
      try {
        sectionContent = await this.getSectionContent(url.toString());
      } catch (err) {
        if (err.name === 'AbortError') return;
        throw err;
      }

      // Updates Variant Data
      this.sectionContent = sectionContent;
      this.updateVariantData(sectionContent);
      this.updateOptionData(sectionContent);

      // Updates UI
      this.updateOptionsAvailability();
      this.updateLabels();
      this.updateDescriptions();
      this.dispatchEvent(this._variantChanged);
    }

    async onCombinedListingChange(e) {
      this.element = e.target;
      this.dispatchEvent(this._combinedListingWorking);
      const sectionId = this.sectionId;
      let url = new URL(`${window.location.origin}${e.target.dataset.productUrl}`);

      this.updateDatasetUrl(url.pathname);

      this.optimisticUpdateLabels(e);

      // Update URL
      const optionValues = this.selectedOptionValues;
      url.searchParams.set('option_values', optionValues);

      // Update browser URL unless inside a modal
      if (!this.noHistory) {
        window.history.replaceState({}, '', url.toString());
      }

      // Update Section ID
      url.searchParams.set('section_id', sectionId);

      let sectionContent;
      try {
        sectionContent = await this.getSectionContent(url.toString());
      } catch (err) {
        if (err.name === 'AbortError') return;
        throw err;
      }

      this.sectionContent = sectionContent;
      this.dispatchEvent(this._combinedListingChanged);
    }

    async onInlineCombinedListingChange(e) {
      this.element = e.target;
      this.dispatchEvent(this._combinedListingWorking);
      const sectionId = this.dataset.scriptsSection || 'product-scripts';
      let url = new URL(`${window.location.origin}${e.target.dataset.productUrl}`);

      this.updateDatasetUrl(url.pathname);

      this.optimisticUpdateLabels(e);

      // Update URL
      const optionValues = this.selectedOptionValues;
      url.searchParams.set('option_values', optionValues);

      // Update browser URL unless inside a modal
      if (!this.noHistory) {
        window.history.replaceState({}, '', url.toString());
      }

      // Update Section ID
      url.searchParams.set('section_id', sectionId);

      let sectionContent;
      try {
        sectionContent = await this.getSectionContent(url.toString());
      } catch (err) {
        if (err.name === 'AbortError') return;
        throw err;
      }

      this.updateVariantData(sectionContent);
      this.updateOptionData(sectionContent);
      this.updateOptionIds();
      this.updateOptionUrls();
      this.updateOptionsAvailability();
      this.updateLabels();
      this.updateDescriptions();
      this.sectionContent = sectionContent;
      this.dispatchEvent(this._combinedListingChanged);
    }

    updateOptionIds() {
      const options = this.options;
      if (!Array.isArray(options) || !options.length) {
        return;
      }
      const hasKeys = options.some((o) => o && o.position != null && o.value != null);
      if (!hasKeys) {
        return;
      }
      const byKey = new Map();
      for (const o of options) {
        if (o == null || o.position == null || o.value == null) {
          continue;
        }
        byKey.set(`${o.position}:${o.value}`, o);
      }
      for (const input of this.querySelectorAll('input[type="radio"]')) {
        const fieldset = input.closest('fieldset');
        if (!fieldset?.dataset.optionPosition) {
          continue;
        }
        const position = parseInt(fieldset.dataset.optionPosition, 10);
        if (Number.isNaN(position)) {
          continue;
        }
        const option = byKey.get(`${position}:${input.value}`);
        if (option) {
          input.dataset.optionValueId = String(option.id);
        }
      }
    }

    updateOptionUrls() {
      // filter options with product_url not null
      const optionsWithProductUrl = this.options.filter((option) => option.product_url !== null);
      for (const option of optionsWithProductUrl) {
        const input = this.querySelector(`input[data-option-value-id="${option.id}"]`);
        if (input) input.setAttribute('data-product-url', option.product_url);
      }
    }

    updateDatasetUrl(url) {
      if (url) this.dataset.url = url.toString();
    }

    updateVariantData(doc) {
      const json = this.querySelector('script.variant-data[type="application/json"]');
      const newJson = doc.querySelector('script.variant-data[type="application/json"]');
      if (json && newJson) json.textContent = newJson.textContent;
    }

    updateOptionData(doc) {
      const json = this.querySelector('script.option-data[type="application/json"]');
      const newJson = doc.querySelector('script.option-data[type="application/json"]');
      if (json && newJson) json.textContent = newJson.textContent;
    }

    async getSectionContent(url) {
      this._fetchController?.abort();
      this._fetchController = new AbortController();
      const response = await fetch(url, {signal: this._fetchController.signal});
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    get selectedOptionValues() {
      return Array.from(this.querySelectorAll('fieldset input:checked'))
        .map(({dataset}) => dataset.optionValueId)
        .filter(Boolean)
        .join(',');
    }

    get variant() {
      const json = this.querySelector('script.variant-data[type="application/json"]');
      if (!json) return {};

      try {
        return JSON.parse(json.textContent);
      } catch (e) {
        console.error('Invalid variant JSON', e);
        return {};
      }
    }

    get options() {
      const json = this.querySelector('script.option-data[type="application/json"]');
      if (!json) return [{}];

      try {
        return JSON.parse(json.textContent);
      } catch (e) {
        console.error('Invalid option JSON', e);
        return [{}];
      }
    }

    updateOptionsAvailability() {
      const alwaysAvailable = this.hasAttribute('data-always-available');
      const soldOutLabel = KROWN?.settings?.locales?.products_sold_out_variant || 'Sold out';

      for (const option of this.options) {
        const input = this.querySelector(`input[data-option-value-id="${option.id}"]`);
        if (!input) continue;

        const available = alwaysAvailable || !!option.available;
        const item = input.closest('.product-variant__item');

        input.setAttribute('data-product-available', available);
        input.classList.toggle('disabled', !available);
        input.disabled = false;

        if (available) {
          input.removeAttribute('aria-disabled');
        } else {
          input.setAttribute('aria-disabled', 'true');
        }

        const availabilityEl = item?.querySelector('.option__availability');
        if (availabilityEl) {
          availabilityEl.textContent = available ? '' : `, ${soldOutLabel}`;
        }

        const swatchImg = item?.querySelector('.product-variant__item-swatch--color');
        if (swatchImg) {
          swatchImg.alt = available ? input.value : `${input.value}, ${soldOutLabel}`;
        }

        if (option.product_url) {
          input.setAttribute('data-product-url', option.product_url);
        }

        const exists = alwaysAvailable || option.exists !== false;
        input.hidden = !exists;
      }
    }

    optimisticUpdateLabels(e) {
      const target = e.target;
      const label = target.closest('fieldset').querySelector('legend [data-js-current-selection]');
      if (label) label.textContent = target.value;
    }

    updateLabels() {
      // Look up the variant label by the element's data-js-option attribute (the
      // option position) instead of its index. Indexing by DOM order breaks when
      // extra fieldsets (e.g. the limited-edition swatch group) are interleaved
      // between options, which would shift every subsequent label off by one.
      this.querySelectorAll('[data-js-dynamic-option-label]').forEach((el) => {
        const position = el.dataset.jsOption;
        if (!position) return;
        el.textContent = this.variant[`label-${position}`] || '';
      });
    }

    updateDescriptions() {
      this.querySelectorAll('[data-js-dynamic-option-description]').forEach((el) => {
        const position = el.dataset.jsOption;
        if (!position) return;
        el.textContent = this.variant[`description-${position}`] || '';
      });
    }
  }

  customElements.define('product-variants', ProductVariants);
}

if (typeof ProductForm !== 'function') {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.cartType = this.hasAttribute('data-ajax-cart') ? 'ajax' : 'page';
      this.submitButton = this.querySelector('[type="submit"]');
      this.variantInput = this.querySelector('input[name="id"]');
      this.ADD_TO_CART = new Event('add-to-cart');

      // Hydrate variant input from template if needed
      if (!this.variantInput) {
        const template = this.querySelector('template');
        if (template) {
          this.form.append(template.content.cloneNode(true));
          this.variantInput = this.querySelector('input[name="id"]');
        }
      }

      // Listen to variant events from sibling product-variants
      const section = this.closest('.shopify-section') || this.closest('pmd-content') || this.parentElement;
      this.productVariants = section?.querySelector('product-variants');
      if (this.productVariants) {
        this.productVariants.addEventListener('variant:working', this.onVariantWorking.bind(this));
        this.productVariants.addEventListener('variant:changed', this.onVariantChanged.bind(this));
        // Apply initial variant state (BIS form visibility, etc.)
        this.onVariantChanged({target: this.productVariants});
      }

      // Submit handler
      if (!document.body.classList.contains('template-cart') || this.hasAttribute('data-force-form')) {
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }
    }

    // -- Variant state handlers --

    onVariantWorking() {
      this.submitButton?.classList.add('working');
    }

    onVariantChanged(e) {
      this.submitButton?.classList.remove('working');

      const variant = e.target.variant;
      const buttonText = this.submitButton?.querySelector('[data-js-product-add-to-cart-text]');
      const bisForm = this.querySelector('[data-js-bis-form]');
      if (!this.submitButton || !buttonText) return;

      // No variant found (incomplete selection or no match)
      if (!variant || !variant.id) {
        this.submitButton.classList.add('disabled');
        this.submitButton.disabled = true;
        buttonText.textContent = KROWN.settings.locales.unavailable || 'Unavailable';
        if (bisForm) bisForm.hidden = true;
        return;
      }

      // Update hidden variant ID
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      // Toggle BIS form
      if (bisForm) bisForm.hidden = variant.available;

      // Available variant
      if (variant.available) {
        this.submitButton.classList.remove('disabled');
        this.submitButton.disabled = false;

        if (this.dataset.freeProduct === 'true') {
          buttonText.textContent = 'Add For Free';
        } else if (buttonText.hasAttribute('data-show-preorder-wording')) {
          buttonText.textContent = KROWN.settings.locales.preorder || 'Pre-order';
        } else {
          buttonText.textContent = KROWN.settings.locales.add_to_cart || 'Add to cart';
        }
        return;
      }

      // Unavailable variant (sold out / coming soon)
      this.submitButton.classList.add('disabled');
      this.submitButton.disabled = true;

      if (buttonText.hasAttribute('data-show-coming-soon-wording')) {
        buttonText.textContent = 'Coming soon';
      } else {
        buttonText.textContent = KROWN.settings.locales.sold_out || 'Sold out';
      }
    }

    // -- Add to cart --

    async onSubmitHandler(e) {
      e.preventDefault();

      const giftCardRecipient = this.querySelector('gift-card-recipient');
      if (giftCardRecipient?.validate && !giftCardRecipient.validate()) {
        return;
      }

      this.submitButton?.classList.add('working');

      const body = new URLSearchParams(new FormData(this.form)).toString() + '&sections=helper-cart';

      try {
        const response = await fetch(`${KROWN.settings.routes.cart_add_url}.js`, {
          body,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest',
          },
          method: 'POST',
        });

        const data = await response.json();

        if (data.status === 422) {
          if (giftCardRecipient?.showFormError) {
            giftCardRecipient.showFormError(typeof data.description === 'string' ? data.description : data.message);
            return;
          }

          const alert = document.createElement('span');
          alert.className = 'alert alert--error';
          alert.innerHTML = typeof data.description === 'string' ? data.description : data.message;

          if (this.cartType === 'page') {
            document.getElementById('product-page-form-error-cart-alert')?.remove();
            alert.style.marginTop = '1em';
            alert.style.marginBottom = '0';
            alert.id = 'product-page-form-error-cart-alert';
            this.form.parentElement.append(alert);
            return;
          }

          this.updateCart(data.sections?.['helper-cart'], alert);
          return;
        }

        if (this.cartType === 'page') {
          document.location.href = KROWN.settings.routes.cart_url;
          return;
        }

        this.updateCart(data.sections?.['helper-cart']);
        this.dispatchEvent(this.ADD_TO_CART);

        const screenReader = document.getElementById('screen-reader-info');
        if (screenReader) {
          screenReader.innerText = KROWN.settings.locales.cart_announcement;
          setTimeout(() => (screenReader.innerText = ''), 1000);
        }
      } catch (err) {
        console.error('[product-form] Submit error:', err);
      } finally {
        this.submitButton?.classList.remove('working');
        window.tfSizeSampling?.();
      }
    }

    updateCart(html, alert) {
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cartItems = document.getElementById('AjaxCartForm');
      if (!cartItems) return;

      cartItems.innerHTML = doc.getElementById('AjaxCartForm').innerHTML;
      cartItems.ajaxifyCartItems();

      if (alert) cartItems.querySelector('form')?.prepend(alert);

      document.getElementById('AjaxCartSubtotal').innerHTML = doc.getElementById('AjaxCartSubtotal').innerHTML;

      document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
        el.textContent = document.querySelector('#AjaxCartForm [data-cart-count]').textContent;
      });
      document.querySelectorAll('[data-header-cart-total]').forEach((el) => {
        el.textContent = document.querySelector('#AjaxCartForm [data-cart-total]').textContent;
      });

      document.getElementById('cart-recommendations-sidebar')?.generateRecommendations();
      document.getElementById('site-cart-sidebar')?.show();
    }
  }

  if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductForm);
  }
}

if (!customElements.get('klaviyo-notify-me')) {
  const KLAVIYO_COMPANY_ID = 'SkvjyW';
  const KLAVIYO_ENDPOINT = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;

  class KlaviyoNotifyMe extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.button = this.querySelector('button');
      this.email = this.querySelector('input[type="email"]');
      this.variantInput = this.querySelector('input[name="variant_id"]');
      this.successMessage = this.querySelector('[js-success-message]');

      if (!this.form || !this.button || !this.email || !this.successMessage) return;

      this.form.addEventListener('submit', (e) => this.submit(e));
      this.email.addEventListener('input', () => this.reset());

      // Sync variant ID from sibling product-variants
      const section = this.closest('.shopify-section') || this.closest('pmd-content') || document;
      const productVariants = section.querySelector('product-variants');
      if (productVariants && this.variantInput) {
        productVariants.addEventListener('variant:changed', (e) => {
          const variant = e.target?.variant;
          if (variant?.id) this.variantInput.value = variant.id;
          this.reset();
        });
      }
    }

    reset() {
      this.form.classList.remove('visually-hidden');
      this.successMessage.classList.add('visually-hidden');
      this.button.classList.remove('working');
      this.email.disabled = false;
    }

    async submit(e) {
      e.preventDefault();
      if (!this.email.validity.valid || !this.variantInput?.value) return;

      this.button.classList.add('working');
      this.email.disabled = true;

      try {
        const response = await fetch(KLAVIYO_ENDPOINT, {
          method: 'POST',
          headers: {Accept: 'application/json', 'Content-Type': 'application/json', Revision: '2024-06-15'},
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {data: {type: 'profile', attributes: {email: this.email.value.trim()}}},
                channels: ['EMAIL'],
              },
              relationships: {
                variant: {data: {type: 'catalog-variant', id: `$shopify:::$default:::${this.variantInput.value}`}},
              },
            },
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.form.classList.add('visually-hidden');
        this.successMessage.classList.remove('visually-hidden');
      } catch (err) {
        console.error('[klaviyo-notify-me]', err);
        this.reset();
      }
    }
  }

  customElements.define('klaviyo-notify-me', KlaviyoNotifyMe);
}

if (typeof GiftCardRecipient !== 'function') {
  class GiftCardRecipient extends HTMLElement {
    constructor() {
      super();

      const properties = Array.from(this.querySelectorAll('[name*="properties"]'));
      const checkboxPropertyName = 'properties[__shopify_send_gift_card_to_recipient]';
      const timezoneOffsetPropertyName = 'properties[__shopify_offset]';

      this.recipientCheckbox = properties.find((input) => input.name === checkboxPropertyName);
      this.timezoneOffsetProperty = properties.find((input) => input.name === timezoneOffsetPropertyName);
      this.sendOnProperty = properties.find((input) => input.name === 'properties[Send on]');
      this.recipientOtherProperties = properties.filter((input) => input.name !== checkboxPropertyName);
      this.recipientFieldsContainer = this.querySelector('.gift-card-recipient__fields');
      this.formError = this.querySelector('[data-gift-card-recipient-form-error]');

      this.setSendOnDateConstraints();

      if (this.timezoneOffsetProperty) {
        this.timezoneOffsetProperty.value = new Date().getTimezoneOffset().toString();
      }

      if (this.recipientCheckbox) {
        this.recipientCheckbox.addEventListener('change', this.synchronizeProperties.bind(this));
      }
      properties.forEach((property) => {
        property.addEventListener('input', () => this.clearFormError());
        property.addEventListener('change', () => this.clearFormError());
      });
      this.synchronizeProperties();
    }

    synchronizeProperties() {
      this.recipientOtherProperties.forEach((property) => (property.disabled = !this.recipientCheckbox.checked));
      this.recipientFieldsContainer.classList.toggle('hide', !this.recipientCheckbox.checked);
    }

    setSendOnDateConstraints() {
      if (!this.sendOnProperty) return;

      const minDaysFromToday = Number.parseInt(this.sendOnProperty.dataset.minDaysFromToday, 10);
      const maxDaysFromToday = Number.parseInt(this.sendOnProperty.dataset.maxDaysFromToday, 10);

      if (!Number.isNaN(minDaysFromToday)) {
        this.sendOnProperty.min = this.getLocalDateString(minDaysFromToday);
      }

      if (!Number.isNaN(maxDaysFromToday)) {
        this.sendOnProperty.max = this.getLocalDateString(maxDaysFromToday);
      }
    }

    getLocalDateString(daysFromToday) {
      const date = new Date();
      date.setDate(date.getDate() + daysFromToday);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }

    validate() {
      const fieldsToValidate = [this.recipientCheckbox, ...this.recipientOtherProperties].filter(
        (property) => property && !property.disabled
      );

      for (const property of fieldsToValidate) {
        if (!property.checkValidity()) {
          this.showFormError(this.getValidationMessage(property));
          property.focus();
          return false;
        }
      }

      return true;
    }

    getValidationMessage(property) {
      if (property === this.sendOnProperty && property.validity.rangeOverflow) {
        return 'Send on must be within 90 days.';
      }

      if (property === this.sendOnProperty && property.validity.rangeUnderflow) {
        return 'Send on must be tomorrow or later.';
      }

      if (property.validity.valueMissing) {
        const label = this.querySelector(`label[for="${property.id}"]`)?.textContent.trim() || property.placeholder;
        return label ? `${label} is required.` : property.validationMessage;
      }

      return property.validationMessage;
    }

    clearFormError() {
      if (!this.formError) return;

      this.formError.textContent = '';
      this.formError.hidden = true;
    }

    showFormError(message) {
      if (!this.formError) return;

      this.formError.textContent = message;
      this.formError.hidden = false;
      this.formError.focus?.();
    }
  }

  if (typeof customElements.get('gift-card-recipient') == 'undefined') {
    customElements.define('gift-card-recipient', GiftCardRecipient);
  }
}
